import { Files } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import {
  Avatar,
  Button,
  Card,
  Columns,
  ComingSoon,
  Divider,
  DocumentView,
  Icon,
  Image,
  Pressable,
  ScrollView,
  Stack,
  Surface,
  Text,
  TruncatedText,
  Video,
  FileShareSheet,
  useFile,
  useFilePlatform,
  useFormat,
  useLocale,
  useNavigation,
  useSharingApi,
  useT,
  useTr,
  useWorkspace,
} from "@bundle/ui";
import { Shell } from "@bundle/core";
import { useState, type ReactNode } from "react";

export type FileScreenProps = { fileId: string };

const PREVIEW_WIDTH = 720;
const PREVIEW_HEIGHT = 540;

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Stack direction="horizontal" align="start" gap="3">
      <Stack paddingY="0.5">
        <Text variant="footnote" color="text.tertiary" lines={1}>
          {label}
        </Text>
      </Stack>
      <Stack grow align="end">
        {children}
      </Stack>
    </Stack>
  );
}

/** /{ws}/f/{fileId}: a shared file — preview, details, who can access it and what I can do with it. */
export function FileScreen({ fileId }: FileScreenProps) {
  const t = useT();
  const tr = useTr();
  const fmt = useFormat();
  const locale = useLocale();
  const nav = useNavigation();
  const ws = useWorkspace();
  const platform = useFilePlatform();
  const { vm, state: s } = useFile(fileId);
  const link = Shell.formatUrl({ type: "file", ws: ws.slug, fileId });
  // featureFlags.sharing: the SharingContext is provided only when sharing is on (WorkspaceRoot).
  const sharingOn = useSharingApi() !== null;
  const [sharing, setSharing] = useState(false);

  if (s.status === "loading") {
    return (
      <Stack grow align="center" justify="center" testID="file-screen-loading">
        <Text color="text.tertiary">{t("common.loading")}</Text>
      </Stack>
    );
  }
  if (s.status === "notFound") return <ComingSoon icon="lock" title={t("files.page.notFound.title")} body={t("files.page.notFound.body")} />;
  if (s.status === "error" || s.file === undefined) {
    return (
      <Stack grow align="center" justify="center" gap="3" testID="file-screen-error">
        <Text color="text.secondary">{s.offline ? t("offline.bannerNoCache") : t("common.error")}</Text>
        <Button label={t("common.retry")} variant="secondary" onPress={() => vm.refresh()} testID="file-retry" />
      </Stack>
    );
  }
  const file = s.file;
  const p = s.permissions;
  const size = Files.fitWithin(file.width, file.height, PREVIEW_WIDTH, PREVIEW_HEIGHT);

  let preview: ReactNode;
  if (s.preview === "image" && s.previewSrc !== undefined) {
    preview = <Image src={s.previewSrc} alt={file.name} width={size.width} height={size.height} radius="lg" fit="contain" />;
  } else if (s.preview === "video" && s.previewSrc !== undefined) {
    preview = <Video src={s.previewSrc} poster={file.thumbUrl} title={file.name} width={size.width} height={size.height} radius="lg" testID="file-video" />;
  } else if (s.preview === "pdf" && s.previewSrc !== undefined) {
    preview = <DocumentView src={s.previewSrc} title={file.name} testID="file-pdf" />;
  } else {
    preview = (
      <Card padding="8" testID="file-card-preview">
        <Stack grow align="center" justify="center" gap="3">
          <Stack padding="4" radius="xl" background="accent.subtle">
            <Icon name={s.icon as IconName} size="icon.lg" color="accent.text" />
          </Stack>
          <Stack align="center">
            <TruncatedText weight="semibold" mode="words" align="center" testID="file-card-name">
              {file.name}
            </TruncatedText>
          </Stack>
          <Stack align="center">
            <TruncatedText variant="footnote" color="text.secondary" mode="words" align="center">
              {s.previewReason === undefined ? t("files.noPreview") : tr(s.previewReason)}
            </TruncatedText>
          </Stack>
        </Stack>
      </Card>
    );
  }

  return (
    <Stack grow testID="file-screen">
      <Stack direction="horizontal" align="center" gap="3" paddingX="5" paddingY="2.5">
        <Icon name={s.icon as IconName} size="icon.md" color="text.secondary" />
        <Stack grow>
          <TruncatedText variant="headline" weight="semibold" testID="file-name">
            {file.name}
          </TruncatedText>
        </Stack>
        {sharingOn ? <Button label={t("files.share")} icon="users" variant="secondary" size="sm" onPress={() => setSharing(true)} testID="file-share" /> : null}
        <Button label={t("files.copyLink")} icon="link" variant="secondary" size="sm" onPress={() => platform.copyLink(link)} testID="file-copy-link" />
        <Button label={t("files.download")} icon="download" variant="primary" size="sm" disabled={!p.canDownload} onPress={() => platform.download(file)} testID="file-download" />
      </Stack>
      <Divider />
      <ScrollView padding="6" testID="file-scroll">
        <Columns
          stackBelow={720}
          testID="file-columns"
          main={
            <Stack align="stretch" testID="file-preview">
              {preview}
            </Stack>
          }
          side={
          <Surface material="plain" radius="lg" padding="5" testID="file-details">
            <Stack gap="5">
              <Stack gap="3">
                <Row label={t("files.page.size")}>
                  <Text variant="footnote">{Files.formatFileSize(file.size, locale)}</Text>
                </Row>
                <Row label={t("files.page.type")}>
                  <Text variant="footnote">{tr(s.kindLabel)}</Text>
                </Row>
                {s.uploader === undefined ? null : (
                  <Row label={t("files.page.sharedBy")}>
                    <Stack direction="horizontal" align="center" gap="1.5">
                      <Avatar name={s.uploader.name} src={s.uploader.avatarUrl} size="avatar.xs" square={s.uploader.isAgent} />
                      <Text variant="footnote" lines={1}>
                        {s.uploader.name}
                      </Text>
                    </Stack>
                  </Row>
                )}
                {s.at === undefined ? null : (
                  <Row label={t("files.page.when")}>
                    <Text variant="footnote">{fmt.dateTime(s.at)}</Text>
                  </Row>
                )}
                <Row label={t("files.page.where")}>
                  {s.openUrl === undefined ? (
                    <Text variant="footnote" lines={1}>
                      {tr(s.where)}
                    </Text>
                  ) : (
                    <Pressable onPress={() => nav.open(s.openUrl!)} radius="sm" accessibilityLabel={t("files.page.openConversation")} testID="file-where">
                      <Text variant="footnote" color="text.link" lines={1}>
                        {tr(s.where)}
                      </Text>
                    </Pressable>
                  )}
                </Row>
              </Stack>
              <Divider />
              <Stack gap="2" testID="file-access">
                <Text variant="footnote" weight="semibold" color="text.secondary">
                  {t("files.page.access")}
                </Text>
                {s.audience.map((line) => (
                  <Stack key={`${line.key}:${JSON.stringify(line.params)}`} direction="horizontal" align="start" gap="2">
                    <Icon name="users" size="icon.sm" color="text.tertiary" />
                    <Stack grow>
                      <Text variant="footnote">{tr(line)}</Text>
                    </Stack>
                  </Stack>
                ))}
              </Stack>
              <Divider />
              <Stack gap="2" testID="file-you">
                <Text variant="footnote" weight="semibold" color="text.secondary">
                  {t("files.page.you")}
                </Text>
                <Stack direction="horizontal" align="center" gap="2" testID="file-can-view">
                  <Icon name={p.canView ? "checkCircle" : "ban"} size="icon.sm" color={p.canView ? "status.success" : "status.danger"} />
                  <Stack grow>
                    <Text variant="footnote">{t("files.page.view")}</Text>
                  </Stack>
                </Stack>
                <Stack direction="horizontal" align="start" gap="2" testID="file-can-download">
                  <Icon name={p.canDownload ? "checkCircle" : "ban"} size="icon.sm" color={p.canDownload ? "status.success" : "status.danger"} />
                  <Stack grow gap="0.5">
                    <Text variant="footnote">{t("files.download")}</Text>
                    {s.downloadReason === undefined ? null : (
                      <Text variant="caption" color="text.secondary" testID="file-download-reason">
                        {tr(s.downloadReason)}
                      </Text>
                    )}
                  </Stack>
                </Stack>
                {s.openUrl === undefined ? null : (
                  <Pressable direction="horizontal" radius="sm" onPress={() => nav.open(s.openUrl!)} accessibilityLabel={t("files.page.openConversation")} testID="file-open-conversation">
                    <Stack grow direction="horizontal" align="center" gap="2" paddingY="0.5">
                      <Icon name="message" size="icon.sm" color="text.link" />
                      <Stack grow>
                        <Text variant="footnote" color="text.link">
                          {t("files.page.openConversation")}
                        </Text>
                      </Stack>
                    </Stack>
                  </Pressable>
                )}
                {s.joinLabel === undefined ? null : (
                  <Stack direction="horizontal">
                    <Button label={tr(s.joinLabel)} variant="secondary" size="sm" loading={s.joining} onPress={() => void vm.join()} testID="file-join" />
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Surface>
          }
        />
      </ScrollView>
      {sharing ? <FileShareSheet fileId={fileId} onClose={() => setSharing(false)} /> : null}
    </Stack>
  );
}
